import React from 'react'
import { getCgiUrl } from '../../helpers/url'
import { BaseWidgetProps, ConfigurablePropsFromDescription, type WidgetDescription } from '../../helpers/widget'
import { useVoteToDisableUserActions } from '../../redux/current-viewer'
import Area from '../Area'
import { SparqlTriggerExecutor } from '../SparqlTriggerExecutor'
import { type ViewerConfig } from '../Viewer'
import { usePubsub } from '../../redux/pubsub'
import { z } from 'zod'

const VALID_CONFIG_SCHEMA = z.object({})

const widgetOptions = [
  {
    label: 'app config to use as component',
    name: 'component',
    type: 'import-app-json',
  }
] as const satisfies WidgetDescription['variables']

export const componentWidgetDescriptor = {
  label: 'Component Widget [UNSTABLE]',
  name: 'Component', // without "Widget" because that's already added when hovering
  component: ComponentWidget,
  variables: widgetOptions,
} as const satisfies WidgetDescription

type WidgetProps
  = BaseWidgetProps
  & ConfigurablePropsFromDescription<typeof widgetOptions>

type LastApplied = {
  config?: ViewerConfig
}

const lastAppliedInit: LastApplied = {config: undefined}

export function ComponentWidget(props: WidgetProps) {
  if (!props.component?.appConfig) return (
    <div>
      Provide an app config
    </div>
  )

  if (!VALID_CONFIG_SCHEMA.safeParse(props.component.appConfig).success) return (
    <div>
      Provide a valid app config
    </div>
  )

  const componentConfig = props.component.appConfig as unknown as ViewerConfig // not yet parsing if valid

  return (
    <ValidComponent {...props} config={componentConfig} />
  )
}

function ValidComponent(props: WidgetProps & {config: ViewerConfig}) {
  const voteToDisableUserActions = useVoteToDisableUserActions()
  const [, publish] = usePubsub()
  const lastApplied = React.useRef<LastApplied>(lastAppliedInit)

  React.useEffect(() => {
    if (lastApplied.current.config !== props.config) {
      lastApplied.current.config = props.config
      console.log('component: applying new config')

      if (props.config.parameterValues) {
        const initComponentVars = JSON.parse(props.config.parameterValues) as Record<string, string>
        for (const key in initComponentVars) {
          publish(key, initComponentVars[key])
        }
      }

    }
  }, [props.config, publish])

  return (<>
    {props.config.triggers.map(trigger => (
      <SparqlTriggerExecutor
        signalIsFetching={(isFetching) => {
          if (trigger.voteForUserActionsDisabledWhenLoading)
            voteToDisableUserActions(trigger.label, isFetching)
        }}
        key={trigger.label}
        templateId={trigger.label}
        endpoint={getCgiUrl('sparql', props.repoName, props.repoURL, props.localRepoURL)}
        sparqlTemplate={trigger.sparqlQuery}
        pubVars={props.pubsub}
        publish={props.publish}
      />))}
    <Area {...props}
      notEditable
      config={props.config.mainArea}
      index={0}
    />
  </>)
}